import React, { useState } from 'react';
import moment from 'moment';
import config from 'src/config';
import { AlertTriangle, PanelRightClose, PanelRightOpen } from 'lucide-react';
import MapCanvas from 'src/shared/components/map/MapCanvas';
import MapPointLabel from 'src/shared/components/map/MapPointLabel';
import useMaplibreBasemap from 'src/shared/components/map/useMaplibreBasemap';
import { signEngineTileRequests } from 'src/shared/components/site-tile-map/siteTileRequestTransform';
import type { Map as MapLibreMap, MapLayerMouseEvent } from 'maplibre-gl';
import { fieldsOf, type FeatureField } from 'src/shared/utils/mapFeatureFields';
import type { StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { buildCatalogueMapStyle, cameraForItems, tileLayerIdsFor } from './catalogueMapStyle';
import { tileDrawingIssueFor } from './tileDrawingIssue';

type CatalogueMapProps = {
    /** Everything in force at `atInstant`. A mosaic resolves to several at once. */
    items: StacItem[];
    collectionId: string;
    atInstant: Date;
    /** The version whose detail is open. */
    /** What the picked version holds, floated over the map rather than placed beneath it. */
    detail?: React.ReactNode;
    /** What the engine read out of each version's archive, keyed by version id. */
    servedLayers?: Record<string, string[]>;
    /** Why the engine could not say, if it could not. */
    layersError?: string;
};

const formatDay = (at: Date) => moment.utc(at).format('D MMM YYYY');

/** Sized to what is left of the viewport, with a floor so it is never squeezed to nothing. */
/**
 * The detail of the picked version, folded away on request.
 *
 * It covers a quarter of the map, and a reader who came to look at the data needs it out of
 * the way — and back again without losing which version they had picked, which is why it folds
 * rather than closing.
 */
const DetailPanel = ({ detail }: { detail: React.ReactNode }) => {
    const [isOpen, setIsOpen] = useState(true);

    return (
        <div
            className={`absolute right-3 top-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-col rounded-lg border border-border bg-white/95 shadow-portfolio-summary-card backdrop-blur ${
                isOpen ? 'w-[26rem]' : 'w-auto'
            }`}
            data-testid='catalogue-map-detail'
        >
            <button
                type='button'
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Hide the version detail' : 'Show the version detail'}
                onClick={() => setIsOpen(open => !open)}
                data-testid='catalogue-map-detail-toggle'
                className='flex items-center gap-1 self-end rounded p-2 text-muted-foreground hover:text-primary'
            >
                {isOpen ? (
                    <PanelRightClose className='h-4 w-4' aria-hidden='true' />
                ) : (
                    <PanelRightOpen className='h-4 w-4' aria-hidden='true' />
                )}
            </button>
            {isOpen && (
                <div className='min-h-0 flex-1 overflow-y-auto px-4 pb-4' data-testid='catalogue-map-detail-content'>
                    {detail}
                </div>
            )}
        </div>
    );
};

const shell = (children: React.ReactNode, detail?: React.ReactNode) => (
    <div
        className='relative h-[calc(100vh-18rem)] min-h-[480px] w-full overflow-hidden rounded-xl border border-border bg-white'
        data-testid='catalogue-map'
    >
        {children}
        {/* Outside the branches above: a version stays picked even when nothing is in force at
            the instant the marker points at, and its detail is still what the reader asked for. */}
        {detail && <DetailPanel detail={detail} />}
    </div>
);

const centred = (testId: string, message: string, detail?: React.ReactNode) =>
    shell(
        <div className='flex h-full items-center justify-center p-6'>
            <p className='max-w-sm text-center text-sm text-muted-foreground' data-testid={testId}>
                {message}
            </p>
        </div>,
        detail
    );

/**
 * The catalogue as it stood at one instant.
 *
 * One map for the whole dataset rather than one per version: resolution answers with a *set*
 * — a mosaic gives one item per footprint at a single instant (ADR-016 decision 10) — so what
 * is on screen belongs to the instant, not to any one version.
 *
 * The versions behind it sit in a panel over the map rather than beside it, so the map keeps
 * the room.
 */
const CatalogueMap = ({ items, collectionId, atInstant, detail, servedLayers, layersError }: CatalogueMapProps) => {
    /**
     * What the pointer is over, if anything.
     *
     * Which fields a feature carries is the dataset's business, so the popup enumerates
     * whatever is there rather than naming any of them.
     */
    const [hovered, setHovered] = useState<{ x: number; y: number; fields: FeatureField[] } | null>(null);
    const basemap = useMaplibreBasemap();
    const camera = cameraForItems(items);

    if (items.length === 0) {
        return centred(
            'catalogue-map-empty',
            `No version was in force on ${formatDay(atInstant)}. Move the marker along the timeline to an instant one was.`,
            detail
        );
    }

    if (!camera) {
        return centred(
            'catalogue-map-no-footprint',
            'The version in force states no footprint, so there is nowhere to place it on a map.',
            detail
        );
    }

    const issue = layersError ? 'unreadable' : tileDrawingIssueFor(items, servedLayers ?? {});

    const drawnLayers = tileLayerIdsFor(items, servedLayers ?? {});

    const listenForFeatures = (map: MapLibreMap) => {
        drawnLayers.forEach(layerId => {
            map.on('mousemove', layerId, (event: MapLayerMouseEvent) => {
                const [feature] = event.features ?? [];
                map.getCanvas().style.cursor = 'pointer';
                setHovered({
                    x: event.point.x,
                    y: event.point.y,
                    fields: fieldsOf(feature?.properties as Record<string, unknown> | undefined),
                });
            });

            map.on('mouseleave', layerId, () => {
                map.getCanvas().style.cursor = '';
                setHovered(null);
            });
        });
    };
    const style = buildCatalogueMapStyle({
        items,
        collectionId,
        engineApiUrl: config.engineApiUrl,
        basemap,
        servedLayers,
    });

    return shell(
        <div className='h-full w-full' data-testid='catalogue-map-canvas'>
            <MapCanvas style={style} camera={camera} transformRequest={signEngineTileRequests} onMapReady={listenForFeatures} />

            {hovered && (
                <MapPointLabel x={hovered.x} y={hovered.y} testId='catalogue-map-feature'>
                    {hovered.fields.length === 0 ? (
                        <span className='text-muted-foreground'>This feature carries no fields.</span>
                    ) : (
                        <dl className='flex flex-col gap-0.5'>
                            {hovered.fields.map(field => (
                                <div key={field.label} className='flex gap-2'>
                                    <dt className='shrink-0 text-muted-foreground'>{field.label}</dt>
                                    <dd className='truncate font-medium text-foreground'>{field.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </MapPointLabel>
            )}

            {issue && (
                <div
                    className='absolute left-1/2 top-3 z-20 flex max-w-md -translate-x-1/2 items-start gap-2 rounded-md border border-amber-200 bg-amber-50/95 px-3 py-2 shadow-sm backdrop-blur'
                    data-testid='catalogue-map-tile-warning'
                    role='status'
                >
                    <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' aria-hidden='true' />
                    <p className='text-xs text-foreground'>
                        {issue === 'unreadable' ? (
                            <>
                                <span className='font-semibold'>The tile archive could not be read.</span> {layersError} Nothing is
                                requested from it until that is fixed.
                            </>
                        ) : issue === 'no-archive' ? (
                            <>
                                <span className='font-semibold'>This version carries no tile archive.</span> Only its footprint is drawn.
                                Publish a <code>application/vnd.pmtiles</code> asset to see the data itself.
                            </>
                        ) : (
                            <>
                                <span className='font-semibold'>The tile archive declares no layers.</span> A map must name the layer inside
                                it, and the archive&rsquo;s own metadata is the only place that name comes from — so this one cannot be
                                drawn until it is rebuilt with it.
                            </>
                        )}
                    </p>
                </div>
            )}
        </div>,
        detail
    );
};

export default CatalogueMap;
